import { useState, useEffect, useCallback, useContext } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import {
  SidePanel,
  SidePanelType,
  Popup,
  PermissionDenied,
  ResponseError,
  responseErrorsResolver,
  ButtonPrimary,
  ButtonSecondary,
  ButtonPseudo,
  PermissionEnum,
  SidePanelLoader,
  ChangeLogs,
  Tabs,
  Account,
  Response,
  useQueryState,
  useGetList,
  Filters,
  SchemaFilterType,
  AccountingJobStateEnum,
  usePrevious,
} from '@ui/src';
import { useAppSelector, useAppDispatch } from '@store';
import {
  useCancelAccountingJobMutation,
  useLazyGetAccountingJobItemQuery,
  useLazyGetAccountingJobsChangeLogsQuery,
  useStartAccountingJobMutation,
  useUpdateAccountingJobMutation,
  useGetAccountingManagmentAccountsCategoriesQuery,
  useLazyGetAccountingManagmentAccountsListQuery,
  useLazyGetAccountingManagmentJobAccountsQuery,
  useLazyRetryAccountingJobItemQuery,
  useLazyGetAccountingJobsQuery,
} from './store/service';
import {
  clearAccountingManagmentPanel,
  clearAccountingJobAccountsList,
} from './store/slice';
import { UpdateAccountingJobValues } from './types';
import AccountingJobItem from '../../components/accounting-managment/item';
import { schema } from './variables/accounts';
import Accounts from '../../components/accounting-managment/accounts';
import { ActiveTabContext } from '../../../../../components/layout/context/active-tab-context';

import ROUTES from '../../routes';

import cn from './styles.module.css';

let timeout: NodeJS.Timeout;

const EditAccoutingJob = () => {
  const [panelIsOpen, setPanelIsOpen] = useState(false);
  const [globalError, setGlobalError] = useState('');
  const [activeTab, setActiveTab] = useState<'overview' | 'accounts' | 'logs'>(
    'overview',
  );
  const [selectedAccountsList, setSelectedAccountsList] = useState<Account[]>(
    [],
  );
  const [dirtyPopupVisible, setDirtyPopupVisible] = useState(false);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [defaultValues, setDefaultValues] =
    useState<UpdateAccountingJobValues>();
  const [deleteAccountPopupVisible, setDeleteAccountPopupVisible] =
    useState(false);
  const [deleteAllAccounts, setDeleteAllAccounts] = useState(false);
  const [deleteAccountId, setDeleteAccountId] = useState('');
  const [deleteError, setDeleteError] = useState('');
  const [cancelJobPopupVisible, setCancelJobPopupVisible] = useState(false);
  const [newAccountsWasAdded, setNewAccountsWasAdded] = useState(false);

  const { search } = useLocation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { itemId } = useParams();

  const { focused } = useContext(ActiveTabContext);

  const prevFocused = usePrevious(focused);

  const {
    accountingManagment: {
      changeLogs,
      changeLogsPagination,
      accountingJob,
      accountCategories,
      accountingJobAccounts,
      accountingJobAccountsPagination,
      takeCount,
      errorsList,
    },
    reportsDictionaries: { activePermissions },
    administrationDictionaries: { users },
  } = useAppSelector((state) => state);

  const { isLoading: accountsCategoriesLoading } =
    useGetAccountingManagmentAccountsCategoriesQuery(
      'all=true&include=primaryAccounts',
    );

  const [
    cancelJob,
    { isLoading: cancelInProgress, isSuccess: cancelIsSuccess },
  ] = useCancelAccountingJobMutation();
  const [startJob, { isLoading: startInProgress, isSuccess: startIsSuccess }] =
    useStartAccountingJobMutation();
  const [updateJob, { isLoading: updateInProgress }] =
    useUpdateAccountingJobMutation();
  const [updateAccountsList, { isLoading: updateAccountsListInProgress }] =
    useUpdateAccountingJobMutation();
  const [getAccountingJob, { isLoading, isFetching }] =
    useLazyGetAccountingJobItemQuery();
  const [retryGetAccountingJob] = useLazyRetryAccountingJobItemQuery();
  const [
    getChangeLogs,
    { isLoading: changeLogsIsLoading, isFetching: changeLogsIsFetching },
  ] = useLazyGetAccountingJobsChangeLogsQuery();
  const [
    getAccountsList,
    {
      isLoading: accountsListLoading,
      isFetching: accountsListUpdateInProgress,
    },
  ] = useLazyGetAccountingManagmentAccountsListQuery();
  const [getAccountingJobs] = useLazyGetAccountingJobsQuery();
  const [
    getJobAccounts,
    { isLoading: jobAccountsIsLoading, isFetching: jobAccountsIsFetching },
  ] = useLazyGetAccountingManagmentJobAccountsQuery();

  const { filters, sort, onSortChange, onFilterChange } = useQueryState(
    schema,
    true,
  );

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    setError,
    watch,
    getValues,
    formState: { errors, isDirty },
  } = useForm<UpdateAccountingJobValues>();

  const accountsIds = watch('accountsIds');

  const isDirtyState = Boolean(isDirty || selectedAccountsList?.length);

  const { getListAction, onSort, onFilter, setPage } = useGetList<Account>({
    getList: getJobAccounts,
    onFilterChange,
    onSortChange,
    schema,
    withoutChangeSearchParams: true,
  });

  const onClearTimeout = async () => {
    await clearTimeout(timeout);
  };

  const onRetryJob = async (id: string) => {
    await onClearTimeout();

    timeout = setTimeout(async () => {
      await retryGetAccountingJob(id);
      await onRetryJob(id);
    }, 10000);
  };

  const onDuplicate = () => {
    setGlobalError('');
    setPanelIsOpen(false);
    setDeleteAccountId('');
    setDeleteError('');
    setDeleteAllAccounts(false);

    dispatch(clearAccountingManagmentPanel());

    setTimeout(async () => {
      await onClearTimeout();
    });

    setTimeout(() => {
      navigate(`${ROUTES.REPORTS_ACCOUNTING_MANAGMENT_LIST}${search || ''}`);
    }, 100);

    setTimeout(() => {
      navigate(`${ROUTES.REPORTS_ACCOUNTING_MANAGMENT_CREATE}${search || ''}`, {
        state: {
          accountingJob: {
            periodFrom: accountingJob?.periodFrom,
            type: accountingJob?.type,
            accountsIds: accountingJob?.accountsIds || [],
          },
          duplicate: true,
        },
      });
    }, 200);
  };

  const handleClose = () => {
    setGlobalError('');
    setPanelIsOpen(false);
    setDeleteAccountId('');
    setDeleteError('');
    setDeleteAllAccounts(false);

    dispatch(clearAccountingManagmentPanel());

    setTimeout(async () => {
      await onClearTimeout();
    });

    setTimeout(() => {
      navigate(`${ROUTES.REPORTS_ACCOUNTING_MANAGMENT_LIST}${search || ''}`);
    }, 100);
  };

  const getAccountsListAction = useCallback(async (query: string) => {
    if (query && query.length) {
      const response = await getAccountsList(
        `?${query}&all=true&sortProp=fullNumber&sortDir=asc&closed=false`,
      );

      if (response && response.data) {
        const filteredRes = response.data.data.edges.map((item) => item.node);

        setAccounts(filteredRes);
      }
    }
  }, []);

  const onSearch = async (searchText: string) => {
    const results: Account[] = [];

    if (searchText) {
      const response = (await getAccountsList(
        `?search=${searchText}&all=true&closed=false`,
      )) as { data: Response<Account> };

      if (response?.data?.data) {
        response.data.data.edges?.forEach((item) => {
          results.push(item.node);
        });
      }

      setAccounts(results);
    }
  };

  const onDeleteAccounts = async () => {
    setDeleteError('');

    const values = getValues();

    const nextAccountsIds: string[] = deleteAllAccounts
      ? []
      : (values?.accountsIds?.filter(
          (item) => item !== deleteAccountId,
        ) as string[]);

    const response = (await updateAccountsList({
      id: itemId as string,
      body: { accountsIds: nextAccountsIds },
    })) as { error?: ResponseError };

    if (response?.error) {
      responseErrorsResolver({
        errors: response?.error,
        setError: () => {},
        setGlobalError: setDeleteError,
      });
    } else {
      setValue('accountsIds', nextAccountsIds);

      setDeleteAccountPopupVisible(false);

      if (deleteAllAccounts) {
        setDeleteAllAccounts(false);
      } else {
        setDeleteAccountId('');
      }

      reset({
        periodFrom: defaultValues?.periodFrom,
        accountsIds: nextAccountsIds,
      });

      setDefaultValues({
        periodFrom: defaultValues?.periodFrom as string,
        accountsIds: nextAccountsIds,
      });

      if (nextAccountsIds?.length) {
        const nextFilters: Filters = { ...filters };

        nextFilters.ids = {
          value: nextAccountsIds,
          type: SchemaFilterType.STRING,
        };

        nextFilters.take = { value: ['10'], type: SchemaFilterType.STRING };

        getListAction({ filters: nextFilters, sort });
      } else {
        dispatch(clearAccountingJobAccountsList());
      }
    }
  };

  const onSubmit = handleSubmit(async (values) => {
    let nextAccountsIds: string[] = [];

    if (defaultValues?.accountsIds?.length) {
      nextAccountsIds = ([] as string[]).concat(defaultValues.accountsIds);
    }

    if (selectedAccountsList?.length) {
      selectedAccountsList.forEach((item) => {
        nextAccountsIds.push(item.id);
      });
    }

    const body: UpdateAccountingJobValues = {
      periodFrom: values?.periodFrom,
      accountsIds: nextAccountsIds,
    };

    const response = (await updateJob({ id: itemId as string, body })) as {
      error?: ResponseError;
    };

    if (response?.error) {
      responseErrorsResolver({
        errors: response?.error,
        setError,
        setGlobalError,
      });
    } else {
      reset(body);

      setDefaultValues(body);

      if (selectedAccountsList?.length) {
        setSelectedAccountsList([]);
        setNewAccountsWasAdded(true);

        const nextFilters: Filters = { ...filters };

        nextFilters.ids = {
          value: nextAccountsIds,
          type: SchemaFilterType.STRING,
        };

        nextFilters.take = { value: ['10'], type: SchemaFilterType.STRING };

        getListAction({ filters: nextFilters, sort });
      }
    }
  });

  const onCancelJob = async () => {
    await cancelJob(itemId as string);
  };

  const onStartJob = async () => {
    const response = (await startJob(itemId as string)) as {
      error?: ResponseError;
    };

    if (response?.error) {
      responseErrorsResolver({
        errors: response?.error,
        setError: () => {},
        setGlobalError,
      });
    }
  };

  const getChangeLogsAction = useCallback(
    (cursor?: string) => {
      getChangeLogs(
        `?take=20&entityType=ACCOUNTING_JOB&entityId=${itemId}${
          cursor ? `&cursor=${cursor}` : ''
        }`,
      );
    },
    [itemId],
  );

  useEffect(() => {
    setPanelIsOpen(true);

    if (
      activePermissions?.includes(
        PermissionEnum.REPORTS_ACCOUNTING_JOBS_LITS,
      ) &&
      itemId
    ) {
      getAccountingJob(itemId);
    }
  }, [activePermissions, itemId]);

  useEffect(() => {
    if (
      accountingJob &&
      (accountingJob.state === AccountingJobStateEnum.IN_PROGRESS ||
        accountingJob.state === AccountingJobStateEnum.NEW ||
        accountingJob.state === AccountingJobStateEnum.QUEUED)
    ) {
      onRetryJob(accountingJob.id);
    }
  }, [accountingJob]);

  useEffect(() => {
    if (!focused && prevFocused) {
      setTimeout(async () => {
        await onClearTimeout();
      });
    } else if (
      focused &&
      prevFocused !== null &&
      !prevFocused &&
      accountingJob &&
      (accountingJob.state === AccountingJobStateEnum.IN_PROGRESS ||
        accountingJob.state === AccountingJobStateEnum.NEW ||
        accountingJob.state === AccountingJobStateEnum.QUEUED)
    ) {
      onRetryJob(accountingJob.id);
    }
  }, [focused, prevFocused]);

  useEffect(() => {
    if (
      activeTab === 'accounts' &&
      activePermissions?.includes(PermissionEnum.ACCOUNTS_LIST) &&
      accountingJob &&
      accountingJob?.accountsIds?.length &&
      !accountingJobAccounts
    ) {
      const nextFilters: Filters = { ...filters };

      nextFilters.ids = {
        value: accountingJob?.accountsIds,
        type: SchemaFilterType.STRING,
      };

      nextFilters.take = { value: ['10'], type: SchemaFilterType.STRING };

      getListAction({
        filters: nextFilters,
        sort: { sortProp: 'fullNumber', sortDir: 'asc' },
      });
    }
  }, [activeTab, activePermissions, accountingJob, accountingJobAccounts]);

  useEffect(() => {
    if (cancelIsSuccess || startIsSuccess) {
      if (cancelJobPopupVisible) {
        setCancelJobPopupVisible(false);
      }

      if (startIsSuccess) {
        setTimeout(() => {
          getAccountingJobs(`${search}&take=${takeCount}`);
        }, 500);
      }

      handleClose();
    }
  }, [cancelIsSuccess, startIsSuccess]);

  useEffect(() => {
    if (
      activeTab === 'logs' &&
      activePermissions?.includes(PermissionEnum.AUDIT_LOG_LIST)
    ) {
      getChangeLogsAction();
    }
  }, [activeTab]);

  useEffect(
    () => () => {
      setTimeout(async () => {
        await onClearTimeout();
      });
    },
    [],
  );

  useEffect(() => {
    if (accountingJob) {
      reset({
        periodFrom: accountingJob.periodFrom,
        accountsIds: accountingJob?.accountsIds || [],
      });

      setDefaultValues({
        periodFrom: accountingJob.periodFrom,
        accountsIds: accountingJob?.accountsIds || [],
      });
    }
  }, [accountingJob]);

  let content = <div />;

  if (isLoading || isFetching) {
    content = <SidePanelLoader />;
  } else if (!isLoading && !isFetching) {
    if (
      !activePermissions?.includes(PermissionEnum.REPORTS_ACCOUNTING_JOBS_LITS)
    ) {
      content = <PermissionDenied />;
    } else {
      content = (
        <>
          <Tabs
            tabs={[
              { name: 'Overview', value: 'overview' },
              { name: 'Accounts', value: 'accounts' },
              {
                name: 'Change Logs',
                value: 'logs',
                hidden: !activePermissions.includes(
                  PermissionEnum.AUDIT_LOG_LIST,
                ),
              },
            ]}
            activeTab={activeTab}
            onClick={(tab) =>
              setActiveTab(tab as 'overview' | 'accounts' | 'logs')
            }
            wrapperClassName={cn.accountingJobsTabs}
          />
          {activeTab === 'overview' && (
            <AccountingJobItem
              control={control}
              errors={errors}
              isEditable={Boolean(
                activePermissions?.includes(
                  PermissionEnum.REPORTS_ACCOUNTING_JOBS_EDIT_ACTION,
                ) && accountingJob?.state === AccountingJobStateEnum.NEW,
              )}
              accountingJob={accountingJob}
            />
          )}
          {activeTab === 'accounts' && (
            <Accounts
              filters={filters}
              control={control}
              onFilter={onFilter}
              setPage={setPage}
              pagination={accountingJobAccountsPagination}
              accounts={accountingJobAccounts}
              onSort={onSort}
              sort={sort}
              accountsIds={accountsIds}
              isFetching={jobAccountsIsFetching || jobAccountsIsLoading}
              accountCategories={accountCategories}
              accountsCategoriesLoading={accountsCategoriesLoading}
              accountsIsLoading={
                accountsListLoading || accountsListUpdateInProgress
              }
              onSearchAutocomplete={onSearch}
              setAccountQuery={getAccountsListAction}
              accountsList={accounts?.filter((item) =>
                defaultValues?.accountsIds?.every((id) => item.id !== id),
              )}
              setAccounts={setAccounts}
              setValue={setValue}
              selectedAccountsList={selectedAccountsList}
              setSelectedAccountsList={setSelectedAccountsList}
              setDeleteAccountPopupVisible={setDeleteAccountPopupVisible}
              setDeleteAllAccounts={setDeleteAllAccounts}
              setDeleteAccountId={setDeleteAccountId}
              newAccountsWasAdded={newAccountsWasAdded}
              setNewAccountsWasAdded={setNewAccountsWasAdded}
              isEditable={Boolean(
                activePermissions?.includes(
                  PermissionEnum.REPORTS_ACCOUNTING_JOBS_EDIT_ACTION,
                ) && accountingJob?.state === AccountingJobStateEnum.NEW,
              )}
              errorsList={errorsList}
            />
          )}
          {activeTab === 'logs' && (
            <div>
              {activePermissions?.includes(PermissionEnum.AUDIT_LOG_LIST) ? (
                <div className={cn.changeLogsWrapper}>
                  <ChangeLogs
                    entityType="ACCOUNTING_JOB"
                    entityId={itemId as string}
                    changeLogsIsLoading={changeLogsIsLoading}
                    changeLogs={changeLogs}
                    activePermission={activePermissions}
                    users={users}
                    pagination={changeLogsPagination}
                    changeLogsIsFetching={changeLogsIsFetching}
                    getChangeLogs={getChangeLogsAction}
                  />
                </div>
              ) : (
                <PermissionDenied />
              )}
            </div>
          )}
        </>
      );
    }
  }

  return (
    <>
      <SidePanel
        isOpen={panelIsOpen}
        type={SidePanelType.Customers}
        onClose={() => {
          if (isDirtyState) {
            setDirtyPopupVisible(true);
          } else {
            handleClose();
          }
        }}
        title="Edit Accounting Job"
        errorSidepanel={globalError}
        sidepanelExpandedKey="edit-accounting-job-panel"
        footer={
          <div className={cn.accountingManagmentFooter}>
            {activePermissions?.includes(
              PermissionEnum.REPORTS_ACCOUNTING_JOBS_CREATE_ACTION,
            ) &&
              accountingJob &&
              accountingJob?.state === AccountingJobStateEnum.NEW && (
                <ButtonPrimary
                  text={startInProgress ? 'Loading...' : 'Start Job'}
                  disabled={startInProgress || isDirtyState}
                  onClick={onStartJob}
                  className={cn.startJobButton}
                  bold
                />
              )}
            {activePermissions?.includes(
              PermissionEnum.REPORTS_ACCOUNTING_JOBS_CANCEL_ACTION,
            ) &&
              accountingJob &&
              accountingJob?.state !== AccountingJobStateEnum.FAILED &&
              accountingJob?.state !== AccountingJobStateEnum.CANCELLED &&
              accountingJob?.state !== AccountingJobStateEnum.DONE && (
                <ButtonSecondary
                  text="Cancel Job"
                  onClick={() => setCancelJobPopupVisible(true)}
                  className={`button-white ${cn.saveChanges}`}
                  bold
                />
              )}
            {activePermissions?.includes(
              PermissionEnum.REPORTS_ACCOUNTING_JOBS_EDIT_ACTION,
            ) &&
              accountingJob &&
              accountingJob?.state === AccountingJobStateEnum.NEW && (
                <ButtonSecondary
                  text={updateInProgress ? 'Loading...' : 'Save Changes'}
                  disabled={updateInProgress}
                  onClick={onSubmit}
                  className={`button-white ${cn.saveChanges}`}
                  bold
                />
              )}
            {activePermissions?.includes(
              PermissionEnum.REPORTS_ACCOUNTING_JOBS_CREATE_ACTION,
            ) && (
              <ButtonSecondary
                text="Duplicate"
                onClick={onDuplicate}
                className={`button-white ${cn.saveChanges}`}
                bold
              />
            )}
            <ButtonPseudo
              text="Close"
              onClick={() => {
                if (isDirtyState) {
                  setDirtyPopupVisible(true);
                } else {
                  handleClose();
                }
              }}
              bold
              className={cn.closePanelButton}
            />
          </div>
        }
        closeOnClickOutside
      >
        <div className={cn.accountingJobPanelContent}>{content}</div>
      </SidePanel>
      <Popup
        title="You have unsaved changes"
        description=""
        onCancelClick={() => setDirtyPopupVisible(false)}
        onOkClick={() => {
          setDirtyPopupVisible(false);
          handleClose();
        }}
        cancelButtonText="Cancel"
        okButtonText="Close"
        isVisible={dirtyPopupVisible}
        parent={document.getElementById('dirty-popup') as HTMLElement}
      />
      <Popup
        title="Cancel job?"
        description=""
        onCancelClick={() => setCancelJobPopupVisible(false)}
        onOkClick={onCancelJob}
        cancelButtonText="Close"
        okButtonText={cancelInProgress ? 'Loading...' : 'Yes, Cancel job'}
        isVisible={cancelJobPopupVisible}
        okButtonDisabled={cancelInProgress}
        parent={document.getElementById('dirty-popup') as HTMLElement}
      />
      <Popup
        title={
          deleteAllAccounts
            ? 'Are you sure you want to delete all accounts?'
            : 'Are you sure you want to delete this accounts?'
        }
        description=""
        onCancelClick={() => setDeleteAccountPopupVisible(false)}
        onOkClick={onDeleteAccounts}
        cancelButtonText="Close"
        okButtonDisabled={updateAccountsListInProgress}
        okButtonText={
          updateAccountsListInProgress ? 'Loading...' : 'Yes, delete'
        }
        isVisible={deleteAccountPopupVisible}
        parent={document.getElementById('dirty-popup') as HTMLElement}
      >
        <div className={cn.deleteAccountError}>{deleteError}</div>
      </Popup>
      <div id="dirty-popup" />
    </>
  );
};

export default EditAccoutingJob;
